import { createSlice } from "@reduxjs/toolkit";

// No backend endpoint exists yet for per-user favorites, so "account" storage is still
// localStorage on this browser — just keyed per user instead of one shared bucket, so a guest's
// favorites merge into their account on login instead of leaking into whoever logs in next.
function storageKey(userId) {
  return userId ? `favorites_${userId}` : "favorites_guest";
}

function loadFromStorage(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(key, items) {
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // localStorage unavailable (private mode / quota) — favorites just won't persist.
  }
}

const favoritesSlice = createSlice({
  name: "favorites",
  initialState: { items: loadFromStorage(storageKey(null)), userId: null },
  reducers: {
    toggleFavorite(state, action) {
      const { id, type, data } = action.payload;
      const existingIndex = state.items.findIndex((item) => item.id === id);
      if (existingIndex >= 0) {
        state.items.splice(existingIndex, 1);
      } else {
        state.items.push({ id, type, data, addedAt: Date.now() });
      }
    },
    clearFavorites(state) {
      state.items = [];
    },
    // Dispatched once auth state resolves and whenever it changes (login/logout). On login,
    // folds whatever was saved as a guest into the account's own bucket (union by id).
    syncForUser(state, action) {
      const { userId } = action.payload;
      if (userId) {
        const guestItems = loadFromStorage(storageKey(null));
        const userItems = loadFromStorage(storageKey(userId));
        const merged = [...userItems];
        for (const guestItem of guestItems) {
          if (!merged.some((item) => item.id === guestItem.id)) {
            merged.push(guestItem);
          }
        }
        state.items = merged;
        state.userId = userId;
      } else {
        state.items = loadFromStorage(storageKey(null));
        state.userId = null;
      }
    },
  },
});

export const favoritesActions = favoritesSlice.actions;
export default favoritesSlice.reducer;

export const selectFavorites = (state) => state.favorites.items;
export const selectFavoritesCount = (state) => state.favorites.items.length;
export const selectIsFavorited = (state, id) =>
  state.favorites.items.some((item) => item.id === id);

export const favoritesMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  if (
    action.type === favoritesActions.toggleFavorite.type ||
    action.type === favoritesActions.clearFavorites.type ||
    action.type === favoritesActions.syncForUser.type
  ) {
    const { items, userId } = store.getState().favorites;
    saveToStorage(storageKey(userId), items);
    if (action.type === favoritesActions.syncForUser.type && userId) {
      // Guest bucket has been folded into the account — clear it so it isn't re-merged
      // (e.g. duplicated back in) the next time someone logs out on this browser.
      try {
        window.localStorage.removeItem(storageKey(null));
      } catch {
        // ignore
      }
    }
  }
  return result;
};
